import type { ProblemCopy } from '../../api/problem.ts';
import type { Strings } from '../index.ts';
import { unreadable } from './shared.ts';

/**
 * Die Watch-Seite und die Bedienelemente des Players — der größte Abschnitt, und der, in dem ein
 * übersetztes Wort es aushalten muss, *während ein Video läuft* gelesen zu werden.
 *
 * Zwei Gruppen sind vor jeder Änderung zu lesen. Die **`unplayable`**-Sätze benennen je einen Zustand, auf
 * den ein Leser anders reagieren kann, und zwei davon zu einem „Dieses Video lässt sich nicht abspielen“
 * zusammenzuziehen wirft die einzige Auskunft weg, die die Seite hat.
 *
 * Und die **`menu`**-Werte werden hier aus Zahlen zusammengesetzt und nicht an der Aufrufstelle, genau
 * damit eine Sprache sagen kann, dass sie ihre Dezimalstellen anders schreibt. Deutsch tut das:
 * `speed.rate` und `quality.bitrate` setzen das Komma, weil `toFixed` und die JS-Zahlendarstellung beide
 * einen Punkt liefern. Ein `Intl` wäre hier richtig und ist verboten (`format/index.ts` besitzt es), und
 * für ein Dezimaltrennzeichen genügt reine Zeichenarbeit.
 */
export const watch: Strings['watch'] = {
  unplayable: {
    region: {
      title: 'In dieser Region nicht verfügbar',
      detail:
        'Wer es hochgeladen hat, hat dieses Video auf Länder beschränkt, zu denen das in den Einstellungen gewählte nicht gehört.',
    },
    paid: {
      title: 'Bezahlt oder nur für Mitglieder',
      detail:
        'Dieses Video steckt hinter einem Kauf oder einer Kanalmitgliedschaft, und die kann Visious nicht aufschließen.',
    },
    private: {
      title: 'Privates Video',
      detail: 'Das können nur die Leute sehen, die beim Hochladen eingeladen wurden.',
    },
    removed: { title: 'Video entfernt', detail: 'YouTube liefert dieses Video nicht mehr.' },
    noMse: {
      title: 'Dieser Browser kann kein adaptives Video abspielen',
      detail:
        'Er unterstützt die Media Source Extensions nicht, die adaptives Video braucht, und dieses Video bietet keinen einfacheren Stream als Rückfall an.',
    },
    noSource: {
      title: 'Kein abspielbarer Stream',
      detail:
        'Deine Instanz führt dieses Video als verfügbar und hat dann nichts Abspielbares dahinter.',
    },
    unknownTitle: 'Dieses Video lässt sich nicht abspielen',
    unknownDetail: (reason) => `Deine Instanz meldet es als nicht abspielbar: ${reason}.`,
  },

  progressiveNotice:
    'Dieser Browser kann kein adaptives Video abspielen, deshalb ist das ein einzelner 360p-Stream. Qualität, Tonsprache und Untertitel lassen sich daran nicht steuern.',

  bootstrap: {
    badInstanceTitle: 'Die Instanz-Adresse funktioniert nicht',
    badInstanceDetail: (url) =>
      `${url} ist keine brauchbare Adresse. Prüfe die Instanz unter Einstellungen.`,
    badInstanceFallback: 'Sie',
    noInstanceTitle: 'Keine Instanz eingerichtet',
    noInstanceDetail:
      'Visious braucht eine Invidious-Instanz, mit der es reden kann. Wähle eine unter Einstellungen.',
  },

  problem: {
    notFound: {
      title: 'Video nicht gefunden',
      detail:
        'Die Instanz hat kein solches Video. Vielleicht wurde es entfernt, oder der Link ist falsch.',
      retryable: false,
    },
    removed: {
      title: 'Dieses Video wurde entfernt',
      detail:
        'Es ist nicht mehr auf YouTube. Entweder hat es die hochladende Person entfernt oder YouTube selbst.',
      retryable: false,
    },
    private: {
      title: 'Dieses Video ist privat',
      detail:
        'Es liegt weiterhin auf YouTube, und nur wer es von der hochladenden Person freigegeben bekommen hat, kann es ansehen.',
      retryable: false,
    },
    ageRestricted: {
      title: 'Dieses Video ist altersbeschränkt',
      detail:
        'YouTube gibt es nur an ein angemeldetes Konto heraus, und deine Instanz hat keines.',
      retryable: false,
    },
    membersOnly: {
      title: 'Dieses Video ist nur für Mitglieder',
      detail:
        'YouTube gibt es nur an Mitglieder dieses Kanals heraus, und deine Instanz ist kein Mitglied.',
      retryable: false,
    },
    notYetLive: (when: string | null): ProblemCopy => ({
      title: 'Dieser Livestream hat noch nicht begonnen',
      detail:
        when === null ?
          'Versuche es erneut, sobald er läuft.'
        : `Er beginnt ${when}. Versuche es erneut, sobald er läuft.`,
      retryable: true,
    }),
    unreadable: unreadable('Das Video konnte nicht gelesen werden'),
  },

  watchOn: {
    label: 'Ansehen auf',
    switchPlayer: 'Player wechseln',
    openSite: 'Seite öffnen',
    targets: {
      youtube: 'YouTube',
      instance: 'Invidious',
      peertube: 'PeerTube',
    },
    visious: 'Invidious',
    atTime: (time) => `Bei ${time} starten`,
    peerTubeNotFound: 'Kein Video gefunden',
    peerTubeNoInfo: 'Keine Videodaten',
  },

  share: {
    label: 'Link teilen',
    targets: {
      visious: 'Visious',
      instance: 'Invidious',
      youtube: 'YouTube',
      peertube: 'PeerTube',
    },
    atTime: (time) => `Bei ${time} starten`,
    peerTubeNotFound: 'Kein Video gefunden',
    peerTubeNoInfo: 'Keine Videodaten',
    copied: 'Link kopiert',
    copyFailed: 'Der Link ließ sich nicht kopieren',
  },

  page: {
    playerLabel: 'Videoplayer',
    loadingVideo: 'Dieses Video wird geladen…',
    externalFrameTitle: {
      youtube: 'YouTube-Videoplayer',
      peertube: 'PeerTube-Videoplayer',
    },
    premiereHeading: 'Premiere',
    premiereUnknown: 'Dieses Video hat noch keine Premiere gehabt.',
    premiereAt: (when) => `Premiere ist ${when}.`,
    listLoading: 'Diese Playlist wird geladen…',
    listProblem: 'Diese Playlist konnte nicht geladen werden.',
    listEmpty: 'Diese Playlist enthält keine Videos.',
    listOpen: 'Diese Playlist öffnen',
    playBlocked: 'Der Browser hat die Wiedergabe blockiert. Drücke Abspielen, um sie zu starten.',
    mutedFallback:
      'Der Browser hat die Wiedergabe nur stumm erlaubt. Hebe die Stummschaltung auf, wenn du bereit bist.',
    verified: 'Verifiziert',
    views: (count) => `${count} Aufrufe`,
    likes: (count) => `${count} Likes`,
    dislikes: (count) => `${count} Dislikes`,
    subscribers: (count) => `${count} Abonnenten`,
    showMore: 'Mehr anzeigen',
    showLess: 'Weniger anzeigen',
    chapters: 'Kapitel',
    show: 'Einblenden',
    hide: 'Ausblenden',
    hideChapters: 'Kapitel ausblenden',
    showChapters: 'Kapitel einblenden',
    playerFailedTitle: 'Das Video konnte nicht geladen werden',
    playerFailed: 'Der Player konnte dieses Video nicht laden.',
    playerCodeFailed: 'Der Player konnte nicht geladen werden. Lade die Seite neu.',
  },

  mini: {
    label: 'Verkleinerter Player',
    maximize: 'Zurück zum Video',
    close: 'Verkleinerten Player schließen',
    closeQueue: 'Den Player schließen und die Warteschlange leeren',
    previous: 'Das vorherige Video hier abspielen',
    next: 'Das nächste Video hier abspielen',
    failed: 'Wiedergabe gestoppt. Öffne das Video für Einzelheiten',
    pageOnly: 'Dieses Video läuft nur auf seiner eigenen Seite.',
  },

  chrome: {
    seek: 'Wiedergabeposition',
    position: (current, duration) => `${current} von ${duration}`,
    live: 'LIVE',
    play: 'Abspielen',
    pause: 'Pause',
    back10: '10 Sekunden zurück',
    forward10: '10 Sekunden vor',
    mute: 'Stumm schalten',
    unmute: 'Stummschaltung aufheben',
    volume: 'Lautstärke',
    fullscreen: 'Vollbild',
    exitFullscreen: 'Vollbild beenden',
    theater: 'Kinomodus',
    exitTheater: 'Kinomodus beenden',
    widen: 'Bild auf 21:9 verbreitern',
    exitWiden: 'Zurück zum vollen 16:9-Bild',
    showChapter: (title) => `„${title}“ in der Kapitelliste zeigen`,
    /* Die Einheit bleibt ungetrennt: eine Lücke in einem zweizeichigen Label liest sich als Lücke im
       Label und nicht als SI-Schreibweise. „s“ ist im Deutschen dasselbe Symbol. */
    skipDisc: (seconds) => `${seconds}s`,
    skipBack: (seconds) => `${seconds} Sekunden zurück`,
    skipForward: (seconds) => `${seconds} Sekunden vor`,
  },

  sponsor: {
    skipped: (category) => `${category} übersprungen`,
    unskip: 'Zurückspringen',
    skip: 'Überspringen',
    badgeTitle: (category) =>
      `Von SponsorBlock markiert: ${category} deckt den größten Teil dieses Kapitels ab.`,
  },

  comments: {
    sectionLabel: 'Kommentare',
    sortLabel: 'Kommentare sortieren',
    pinned: 'Angepinnt',
    member: 'Mitglied',
    likes: 'Likes',
    heartedBy: (channel) => `Mit Herz versehen von ${channel}`,
    heartedByCreator: 'Mit Herz versehen von der Urheberin oder dem Urheber',
    edited: '(bearbeitet)',
    repliesFailed: 'Diese Antworten konnten nicht geladen werden.',
    repliesRetry: 'Erneut versuchen',
    showMore: 'Mehr Kommentare anzeigen',
    loading: 'Kommentare werden geladen…',
    loadingMore: 'Wird geladen…',
    loadingReplies: 'Antworten werden geladen…',
    section: {
      hide: 'Ausblenden',
      hideLabel: 'Die Kommentare ausblenden',
      show: 'Einblenden',
      showLabel: 'Die Kommentare einblenden',
    },
  },

  rail: {
    label: 'Empfohlene Videos',
    heading: 'Empfehlungen',
    hide: 'Ausblenden',
    show: 'Einblenden',
    hideRecommendations: 'Empfehlungen ausblenden',
    showRecommendations: 'Empfehlungen einblenden',
    previousLabel: 'Vorherige Empfehlungen',
    nextLabel: 'Mehr Empfehlungen',
    pageOf: (page, pages) => `${page} / ${pages}`,
  },

  playlist: {
    label: 'Playlist',
    heading: 'Playlist',
    hide: 'Ausblenden',
    show: 'Einblenden',
    hideList: 'Die Playlist ausblenden',
    showList: 'Die Playlist einblenden',
    position: (at, total) => `${at} von ${total}`,
    autoplay: 'Autoplay',
    autoplayDetail: 'Das nächste Video dieser Playlist automatisch abspielen, wenn dieses endet',
    reverse: 'Die Reihenfolge umkehren',
    reverseDetail: 'Die Reihenfolge der Playlist umkehren.',
    clear: 'Die Playlist leeren',
    clearDetail: 'Diese Playlist nicht weiter ansehen.',
    unlink: 'Verknüpfung lösen',
    unlinkDetail:
      'Die aktuelle Warteschlange von der Playlist lösen, sodass Änderungen nicht mehr in ihr gespeichert werden.',
    previousEntry: 'Vorheriges Video dieser Playlist',
    nextEntry: 'Nächstes Video dieser Playlist',
    previousLabel: 'Vorherige Einträge',
    nextLabel: 'Mehr Einträge',
    nowPlaying: 'Läuft gerade',
    entryGone: 'Video nicht verfügbar',
    remove: (title) => `„${title}“ aus dieser Playlist entfernen`,
    removeTitle: 'Aus dieser Playlist entfernen',
    removing: 'Wird entfernt…',
    loading: 'Die Playlist wird geladen…',
    problem: 'Die Playlist wurde nicht geladen. Das Video läuft trotzdem.',
  },

  /* Dieselbe Leiste wie bei der Playlist, mit den Wörtern für eine Liste, die nur zu diesem Tab
     gehört. Hier steht nirgends „Playlist“: die Warteschlange wird nirgends gespeichert. Die
     Überschrift der Leiste ist die Ausnahme und steht deshalb nicht hier: auf der Videoseite trägt
     die Warteschlange die Wörter der Playlist-Leiste, und was der Leser offen hat, sagt eine Zeile
     tiefer die Überschrift „Warteschlange“. Autoplay und die Reihenfolge haben eigene Wörter, weil beide
     hier etwas anderes bedeuten: Autoplay ist ein zweiter gespeicherter Schalter, und das Umkehren
     dreht die Einträge wirklich um. */
  queue: {
    autoplay: 'Autoplay',
    autoplayDetail:
      'Das nächste Video der Warteschlange abspielen, wenn dieses endet. Ausgeschaltet hält die Liste nach jedem Video an.',
    reverse: 'Die Reihenfolge umkehren',
    reverseDetail:
      'Das dreht die ganze Warteschlange um. Das Video, das gerade läuft, bleibt auf dem Bildschirm.',
    clear: 'Die Warteschlange leeren',
    clearDetail: 'Das leert die Warteschlange sofort. Die Liste ist nirgendwo sonst gespeichert.',
    /* Der einzige Knopf dieser Zeile, der über den Tab hinausreicht: er gibt die ganze Liste an eine
       Playlist im Konto. Sein `detail` ist der Satz, den die Warteschlange nirgends auf dem Bildschirm
       hatte: die Liste endet mit dem Tab, und so behält man sie. */
    save: {
      label: 'Die Warteschlange als Playlist speichern',
      detail:
        'Die Warteschlange gehört zu diesem Browser-Tab. In einer Playlist gespeichert bleibt sie erhalten.',
      title: 'Die Warteschlange als Playlist speichern',
      close: 'Schließen',
      lede: 'Jedes Video der Warteschlange, das die Playlist noch nicht enthält, kommt an ihr Ende. Danach läuft die Liste in der Reihenfolge der Playlist.',
      signedOutBefore:
        'Playlists liegen in deinem Konto auf der Instanz, das braucht dich also angemeldet.',
      signIn: 'Anmelden',
      signedOutAfter: 'und du kommst direkt hierher zurück.',
      loading: 'Deine Playlists werden geladen…',
      none: 'Du hast noch keine Playlists. Die erste ist einen Titel entfernt.',
      createSubmit: 'Anlegen und speichern',
      reading: 'Die Playlist wird gelesen…',
      saving: 'Wird gespeichert…',
      saved: (videos, already, name) =>
        already === 0
          ? `${videos} in „${name}“ gespeichert.`
          : already === 1
            ? `${videos} in „${name}“ gespeichert, eines war schon darin.`
            : `${videos} in „${name}“ gespeichert, ${already} waren schon darin.`,
      nothingToSave: (name) => `„${name}“ enthält bereits jedes Video der Warteschlange.`,
      refused: (reason) => `${reason}. An der Playlist hat sich nichts geändert.`,
      refusedAfter: (reason, videos, name) =>
        `${reason}. Nur ${videos} sind in „${name}“ angekommen.`,
    },
    previousEntry: 'Vorheriges Video in der Warteschlange',
    nextEntry: 'Nächstes Video in der Warteschlange',
    previousLabel: 'Vorherige Videos',
    nextLabel: 'Mehr Videos',
    nowPlaying: 'Läuft gerade',
    entryGone: 'Video nicht verfügbar',
    remove: (title) => `„${title}“ aus der Warteschlange entfernen`,
    removeTitle: 'Aus der Warteschlange entfernen',
    moveUp: (title) => `„${title}“ in der Warteschlange nach oben schieben`,
    moveUpTitle: 'Nach oben',
    moveDown: (title) => `„${title}“ in der Warteschlange nach unten schieben`,
    moveDownTitle: 'Nach unten',
    removing: 'Wird entfernt…',
    loading: 'Die Warteschlange wird geladen…',
    problem: 'Die Warteschlange wurde nicht geladen. Das Video läuft trotzdem.',
    title: 'Warteschlange',
  },

  menu: {
    audioAndSubtitles: 'Ton und Untertitel',
    audioAndSubtitlesValue: (audio, subtitles) => `${audio}, ${subtitles}`,
    quality: {
      label: 'Qualität',
      auto: 'Automatisch',
      autoDetail: 'Passt sich der Verbindung an',
      autoAt: (height) => `Automatisch (${height}p)`,
      height: (height) => `${height}p`,
      /* `toFixed` liefert einen Punkt, im Deutschen steht dort ein Komma. Mbit/s ist die deutsche
         Schreibweise der Einheit. */
      bitrate: (mbps) => `${mbps.replace('.', ',')} Mbit/s`,
      codecSection: 'Codec',
      codecAuto: 'Automatisch',
      codecForceAvc: 'H.264 erzwingen',
      codecForceAvcDetail: 'Für Hardware, die AV1 nicht dekodieren kann',
      noAvcNote:
        'Dieses Video hat keine H.264-Fassung, deshalb läuft es in dem Format, das es hat.',
      pinMissedNote: (height) =>
        `${height}p gibt es in diesem Video nicht, deshalb läuft die nächstgelegene Stufe.`,
    },
    audio: {
      label: 'Tonspur',
      original: 'Original',
      originalShort: 'Orig.',
      originalEntry: (language) => `${language} - Original`,
      stableVolume: 'Gleichmäßige Lautstärke verwenden',
    },
    subtitles: {
      label: 'Untertitel',
      off: 'Aus',
      autoGenerated: 'Automatisch erzeugt',
    },
    speed: {
      label: 'Wiedergabegeschwindigkeit',
      normal: 'Normal',
      rate: (rate) => `${String(rate).replace('.', ',')}×`,
    },
  },
};
